import { useState } from "react";
import { Link } from "react-router-dom";
import { X, GoogleLogo, AppleLogo, Envelope, Lock, User, EnvelopeSimple } from "@phosphor-icons/react";
import { useAuth } from "../contexts/AuthContext";
import { Capacitor } from "@capacitor/core";
import { useKeyboardHeight } from "../hooks/useKeyboardHeight";
import Input from './ui/Input';

interface LoginModalProps {
  isOpen: boolean;
  onClose: () => void;
}

export default function LoginModal({ isOpen, onClose }: LoginModalProps) {
  const { signInWithGoogle, signInWithApple, signInWithEmail, signUpWithEmail, resetPassword } = useAuth();
  const isIOS = Capacitor.getPlatform() === 'ios';
  const [isSignUp, setIsSignUp] = useState(false);
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [displayName, setDisplayName] = useState("");
  const [error, setError] = useState("");
  const [loading, setLoading] = useState(false);
  const [resetSent, setResetSent] = useState(false);
  const [signUpSuccess, setSignUpSuccess] = useState(false);
  const keyboardHeight = useKeyboardHeight();

  const handleGoogleSignIn = async () => {
    if (isSignUp) sessionStorage.setItem('explicit_terms_accepted', 'true');
    try {
      setLoading(true);
      setError("");
      await signInWithGoogle();
      onClose();
    } catch (err: any) {
      setError(err.message || "Failed to sign in with Google");
    } finally {
      setLoading(false);
    }
  };

  const handleEmailAuth = async (e: React.FormEvent) => {
    e.preventDefault();
    try {
      setLoading(true);
      setError("");

      if (isSignUp) {
        if (!displayName.trim()) {
          setError("Please enter your name");
          return;
        }
        sessionStorage.setItem('explicit_terms_accepted', 'true');
        await signUpWithEmail(email, password, displayName);
        setSignUpSuccess(true);
        return;
      } else {
        await signInWithEmail(email, password);
      }
      onClose();
      // Reset form
      setEmail("");
      setPassword("");
      setDisplayName("");
    } catch (err: any) {
      setError(
        err.message ||
          `Failed to ${isSignUp ? "sign up" : "sign in"}. Please try again.`
      );
    } finally {
      setLoading(false);
    }
  };

  if (!isOpen) return null;

  return (
    <div
      className="fixed inset-0 z-50 flex items-end sm:items-center justify-center bg-black/50 backdrop-blur-sm overflow-y-auto"
      onClick={onClose}
    >
      <div
        className="bg-white dark:bg-gray-900 rounded-t-2xl sm:rounded-2xl shadow-2xl w-full max-w-md sm:mx-4 max-h-[100dvh] sm:max-h-[90vh] overflow-y-auto"
        style={{ paddingBottom: keyboardHeight > 0 ? keyboardHeight : undefined }}
        onClick={(e) => e.stopPropagation()}
      >
        {/* Header */}
        <div className="flex items-center justify-between p-6 border-b border-gray-200 dark:border-gray-700">
          <h2 className="text-2xl font-bold text-gray-800 dark:text-gray-100">
            {isSignUp ? "Create Account" : "Sign In"}
          </h2>
          <button
            onClick={onClose}
            className="p-2 hover:bg-gray-100 dark:hover:bg-gray-700 rounded-lg transition-colors"
          >
            <X size={24} className="text-gray-600 dark:text-gray-400" />
          </button>
        </div>

        {/* Content */}
        <div className="p-6">
          {signUpSuccess ? (
            <div className="text-center py-4">
              <div className="mx-auto w-14 h-14 bg-pink-100 dark:bg-pink-900/30 rounded-full flex items-center justify-center mb-4">
                <EnvelopeSimple size={28} className="text-pink-500" />
              </div>
              <h3 className="text-lg font-semibold text-gray-900 dark:text-gray-100 mb-2">
                Check your email
              </h3>
              <p className="text-sm text-gray-600 dark:text-gray-400 mb-4">
                We sent a verification link to <span className="font-medium">{email}</span>. Click it to verify your account, then come back here.
              </p>
              <button
                onClick={onClose}
                className="w-full px-4 py-3 bg-gradient-to-r from-orange-500 to-pink-500 text-white font-semibold rounded-lg hover:from-orange-600 hover:to-pink-600 transition-all"
              >
                Got it
              </button>
            </div>
          ) : (<>
          {/* Inline legal line — sign-up only, covers all auth methods */}
          {isSignUp && (
            <p className="text-xs text-gray-500 dark:text-gray-400 leading-relaxed mb-5">
              By continuing, you agree to our{' '}
              <Link to="/terms" onClick={onClose} className="text-pink-600 dark:text-pink-400 underline">Terms of Service</Link>
              {' '}and{' '}
              <Link to="/privacy" onClick={onClose} className="text-pink-600 dark:text-pink-400 underline">Privacy Policy</Link>
              .
            </p>
          )}

          {/* Apple Sign In (iOS only) */}
          {isIOS && (
            <button
              onClick={async () => {
                if (isSignUp) sessionStorage.setItem('explicit_terms_accepted', 'true');
                try {
                  setLoading(true);
                  setError("");
                  await signInWithApple();
                  onClose();
                } catch (err: any) {
                  setError(err.message || "Failed to sign in with Apple");
                } finally {
                  setLoading(false);
                }
              }}
              disabled={loading}
              className="w-full flex items-center justify-center space-x-3 px-4 py-3 bg-black dark:bg-white text-white dark:text-black border-2 border-black dark:border-white rounded-lg hover:bg-gray-900 dark:hover:bg-gray-100 transition-all disabled:opacity-50 disabled:cursor-not-allowed mb-3"
            >
              <AppleLogo size={20} weight="fill" />
              <span className="font-medium">
                Continue with Apple
              </span>
            </button>
          )}

          {/* Google Sign In */}
          <button
            onClick={handleGoogleSignIn}
            disabled={loading}
            className="w-full flex items-center justify-center space-x-3 px-4 py-3 bg-white dark:bg-gray-900 border-2 border-gray-300 dark:border-gray-700 rounded-lg hover:bg-gray-50 dark:hover:bg-gray-800 hover:border-gray-400 transition-all disabled:opacity-50 disabled:cursor-not-allowed"
          >
            <GoogleLogo size={20} className="text-gray-700 dark:text-gray-300" />
            <span className="font-medium text-gray-700 dark:text-gray-300">
              Continue with Google
            </span>
          </button>

          {/* Divider */}
          <div className="relative my-6">
            <div className="absolute inset-0 flex items-center">
              <div className="w-full border-t border-gray-300 dark:border-gray-700"></div>
            </div>
            <div className="relative flex justify-center text-sm">
              <span className="px-2 bg-white dark:bg-gray-900 text-gray-500 dark:text-gray-400">or</span>
            </div>
          </div>

          {/* Email/Password Form */}
          <form onSubmit={handleEmailAuth} className="space-y-4">
            {isSignUp && (
              <div>
                <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                  First name
                </label>
                <div className="relative">
                  <User
                    size={20}
                    className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-400 dark:text-gray-500"
                  />
                  <Input
                    variant="default"
                    inputSize="lg"
                    className="w-full pl-10 pr-4"
                    type="text"
                    value={displayName}
                    onChange={(e) => setDisplayName(e.target.value)}
                    placeholder="First name"
                    required={isSignUp}
                  />
                </div>
              </div>
            )}

            <div>
              <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                Email
              </label>
              <div className="relative">
                <Envelope
                  size={20}
                  className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-400 dark:text-gray-500"
                />
                <Input
                  variant="default"
                  inputSize="lg"
                  className="w-full pl-10 pr-4"
                  type="email"
                  value={email}
                  onChange={(e) => setEmail(e.target.value)}
                  placeholder="Enter your email"
                  required
                />
              </div>
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                Password
              </label>
              <div className="relative">
                <Lock
                  size={20}
                  className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-400 dark:text-gray-500"
                />
                <Input
                  variant="default"
                  inputSize="lg"
                  className="w-full pl-10 pr-4"
                  type="password"
                  value={password}
                  onChange={(e) => setPassword(e.target.value)}
                  placeholder="Enter your password"
                  required
                  minLength={6}
                />
              </div>
            </div>

            {!isSignUp && (
              <div className="flex justify-end">
                <button
                  type="button"
                  onClick={async () => {
                    if (!email) { setError('Enter your email first'); return; }
                    try {
                      await resetPassword(email);
                      setResetSent(true);
                      setError('');
                    } catch { setError('Could not send reset email. Check your address.'); }
                  }}
                  className="text-xs text-pink-600 dark:text-pink-400 hover:text-pink-700 dark:hover:text-pink-300"
                >
                  Forgot password?
                </button>
              </div>
            )}

            {resetSent && (
              <div className="p-3 bg-green-50 dark:bg-green-900/20 border border-green-200 rounded-lg text-sm text-green-600">
                Password reset email sent! Check your inbox.
              </div>
            )}

            {error && (
              <div className="p-3 bg-red-50 dark:bg-red-900/20 border border-red-200 rounded-lg text-sm text-red-600">
                {error}
              </div>
            )}

            <button
              type="submit"
              disabled={loading}
              className="w-full mt-2 px-4 py-3.5 bg-gradient-to-r from-orange-500 to-pink-500 text-white font-semibold rounded-lg shadow-md hover:from-orange-600 hover:to-pink-600 transition-all disabled:opacity-50 disabled:cursor-not-allowed"
            >
              {loading
                ? "Loading..."
                : isSignUp
                ? "Create Account"
                : "Sign In"}
            </button>
          </form>

          {/* Toggle Sign Up/Sign In */}
          <div className="mt-6 text-center">
            <button
              onClick={() => {
                setIsSignUp(!isSignUp);
                setError("");
              }}
              className="text-sm text-pink-600 dark:text-pink-400 hover:text-pink-700 dark:hover:text-pink-300 font-medium"
            >
              {isSignUp
                ? "Already have an account? Sign in"
                : "Don't have an account? Sign up"}
            </button>
            {!isSignUp && (
              <p className="mt-2 text-xs text-gray-500 dark:text-gray-400">
                Make Romy personal to you — sign up to save chats, track insights, and more
              </p>
            )}
          </div>
          </>)}
        </div>
      </div>
    </div>
  );
}

